import { Separator } from "@comps/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import Field, { AggregationType, FieldType, shouldDisplayInTooltip } from "@dto/fields";
import { Direction, FieldColumn, INBOUND, Item, Line, ParentKey, PathsResponse, Relation } from "@dto/records";
import { useVirtualizer } from "@tanstack/react-virtual";
import { GRAY_100_COLOR, GRAY_200_COLOR } from "@utils/colors";
import { addLinksFromSeveralSources, deleteLinks, fillPivot, getLinks, getPathByFields, getRecordKeys } from "@utils/data";
import { getFieldByPathKeys, getFieldLeafKey, getRelationDirection, hasOnlyOneValue } from "@utils/model";
import { toastError } from "@utils/toasts";
import { TW_CommonCommandInput, TW_LinkedRecordCommandItem, TW_LinkedRecordCommandItemFieldText, TW_LinkedRecordCommandItemRecordText, TW_LinkedRecordCommandItemSelected, TW_LinkedRecordCommandItemTooltipFieldText, TW_LinkedRecordCommandItemTooltipRecordText, TW_LinkedRecordCommandItemTooltipTrigger, TW_SelectLinkedRecordScrollArea, getWsButtonStyle, useHover } from "@utils/twClasses";
import { idToKey, isArray, itemToString, valueToString } from "@utils/types";
import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useDataStore, useModelStore } from "src/store";
import { CellPosition } from "./GridCell";
import { isLinkChainField, linkedRecordKey } from "./linkChain";


interface LinkEditionFormProps {
    // The link field edited by the cell
    field: Field,
    // Record whose link is edited (for a link chain: the row record, not the parent of the item)
    parentKey: ParentKey,
    item: Item,
    objectKey: string,
    fieldKey: string,
    relationKey: string,
    value: Item,
    updateValue: (value: Item) => void,
    searchValue: string,
    setSearchValue: (searchValue: string) => void,
    searchInputRef: React.RefObject<HTMLInputElement>,
    fieldColumn: FieldColumn,
    position: CellPosition | null,
    format: Map<string, any> | undefined
}

export function LinkEditionForm({ field, parentKey, item, objectKey, fieldKey, relationKey, value, updateValue, searchValue, setSearchValue, searchInputRef, fieldColumn, position, format }: LinkEditionFormProps) {

    const workspace = useModelStore(state => state.selectedWorkspace);
    const objectInfo = useModelStore(state => state.selectedObjectInfos);
    const relationByKeys = useModelStore(state => state.relationByKeys);
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const { t } = useTranslation();
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const linkedField = fieldByKeys?.get(fieldKey);

    const objectByKeys = useModelStore(state => state.objectByKeys);
    const [existingLinks, setExistingLinks] = useState<Relation[]>([]);
    const [records, setRecords] = useState<Line[] | undefined>(undefined);
    // Index, in the lines of the target records, of the column of the displayed
    // field. For a link chain its items may have no key (target record without
    // displayed value), so they cannot be found by field key.
    const [displayedColumnIndex, setDisplayedColumnIndex] = useState<number>(-1);
    const [highlightedIndex, setFocusedIndex] = useState<number>(0);
    const selectedView = useModelStore(state => state.selectedView);
    const metaLines = useDataStore(state => state.metaLines);
    const relation = relationByKeys?.get(relationKey);
    let direction: Direction = getRelationDirection(relation, objectInfo?.object._key);
    const parentRef = useRef<HTMLDivElement>(null);
    const isChain = isLinkChainField(field, fieldByKeys);
    const fieldLeafKey = getFieldLeafKey(fieldKey, fieldByKeys);

    // Item of the displayed field in a line of the target records
    const displayedItem = (line: Line): Item | undefined => {
        if (displayedColumnIndex >= 0 && displayedColumnIndex < line.items.length) {
            return line.items[displayedColumnIndex];
        }
        return line.items.find((item) => item.key.fieldKey === fieldLeafKey);
    };
    // Key of the target record of a line (for a link chain: the parent of the displayed item)
    const lineRecordKey = (line: Line): string | undefined => {
        return linkedRecordKey(displayedItem(line), field, fieldByKeys);
    };

    // Filter records based on search value
    const filteredRecords = records?.filter((line) => {
        if (!searchValue.trim()) return true;

        const searchLower = searchValue.trim().toLowerCase();
        return line.items.some((item) => {
            const field = fieldByKeys?.get(item.key.fieldKey);
            if (!field) return false;

            const fieldValue = itemToString(item, field, AggregationType.NO_AGGR, format, t, "", fieldByKeys);
            return fieldValue.toLowerCase().includes(searchLower);
        });
    }) ?? [];

    // Reset focused index when search value changes or filtered records change
    useEffect(() => {
        setFocusedIndex(0);
    }, [searchValue, filteredRecords.length]);

    const virtualizer = useVirtualizer({
        count: filteredRecords.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 40,
        overscan: 5,
    });

    useEffect(() => {
        if (workspace && parentKey) {

            const relation = relationByKeys?.get(relationKey);
            let direction: Direction = getRelationDirection(relation, objectInfo?.object._key);
            const fields = objectByKeys?.get(objectKey)?.fields?.filter((field: Field) => {
                return field._key === fieldKey || (!field.formula && field.type !== FieldType.Link);
            }) || [] as Field[];
            setRecords(undefined);
            
            // Get the relation filter based on direction
            let relationFilter = null;
            if (relation) {
                if (direction === INBOUND) {
                    relationFilter = relation.srcFilter ?? null;
                } else {
                    relationFilter = relation.destFilter ?? null;
                }
            }
            
            getPathByFields(workspace._key, objectKey, fields, relationByKeys, fieldByKeys, null, relationFilter).then((records) => {
                if (!records || isCompozzError(records)) {
                    console.error("Error fetching records", records);
                    setRecords([]);
                } else {
                    const response = records as PathsResponse;
                    setDisplayedColumnIndex(response.columns?.findIndex((column) => (column.srcField ?? column.field)?._key === fieldKey) ?? -1);
                    setRecords(response.lines);
                }
            });

            let recordKeys: string[] = [parentKey.recordKey];
            const hasPivot = selectedView?.pivot?.pivotPathKey;
            if (hasPivot && !fieldColumn.isPivot) {
                const metaLine = metaLines.get(position!.rowId);
                if (metaLine && metaLine.line.items.length > 0) {
                    const lineRecordKeys = getRecordKeys(metaLine, objectInfo!.object._key);
                    if (lineRecordKeys.length > 0) {
                        recordKeys = lineRecordKeys;
                    }
                }
            }

            getLinks(workspace._key, parentKey.objectKey, relationKey, recordKeys, direction).then((links) => {
                if (isCompozzError(links)) {
                    setExistingLinks([]);
                } else {
                    setExistingLinks(links as Relation[]);
                }
            });

        } else {
            setRecords([]);
        }
    }, [workspace, item?.parentKey, value]);


    // var onlyOneValue = false;
    // if (relation && ((direction === OUTBOUND && relation.destCard === 1) || (direction === INBOUND && relation.srcCard === 1))) {
    //     onlyOneValue = true;
    // }
    const onlyOneValue = hasOnlyOneValue(relation, objectInfo?.object._key);
    const fields = objectByKeys?.get(objectInfo!.object._key)?.fields;

    if (!fields) {
        return null;
    }

    const fieldByPathKeys = getFieldByPathKeys(objectInfo, fields, relationByKeys, fieldByKeys);

    // const updateData = function (newItem: Item) {
    //     updateValue(newItem);
    //     fetchData();
    // }

    // selectedItem is the displayed item of the selected line, selectedRecordKey
    // the key of the selected target record (they differ for a link chain).
    const onRecordSelect = (selectedItem: Item, selectedRecordKey: string) => {

        if (!workspace) {
            console.error("No workspace");
            return;
        }

        const existingLinkKeys = existingLinks.map((link) => {
            if (direction === INBOUND) {
                if (idToKey(link._from) === selectedRecordKey) {
                    return link._key;
                }
            } else {
                if (idToKey(link._to) === selectedRecordKey) {
                    return link._key;
                }
            }
            return null;
        }).filter((key) => key !== null) as string[];
        const isArrayOfLinks = typeof value.value != "string" && isArray(value.value) && (Array.from(value.value).length === 0 || typeof value.value[0] !== "string");
        if (existingLinkKeys.length > 0) {
            deleteLinks(workspace._key, parentKey.objectKey, parentKey.recordKey, relationKey, direction, existingLinkKeys).then((response) => {
                if (response) {
                    if (isCompozzError(response)) {
                        const cError = response as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    }
                } else {
                    if (!isArrayOfLinks || onlyOneValue) {
                        // A cell without link: for a link chain its parent is the row record
                        const newItem: Item = { parentKey: isChain ? parentKey : item.parentKey, value: null, key: { objectKey: "", recordKey: "", fieldKey: "" }, aggregated: false, readOnly: false };
                        updateValue(newItem);
                    } else {
                        const items = value.value as Item[];
                        const newValues = items.filter((value) => {
                            return linkedRecordKey(value, field, fieldByKeys) !== selectedRecordKey;
                        });
                        const newItem: Item = { ...item, value: newValues };
                        updateValue(newItem);
                    }
                    setSearchValue("");
                }
            });
        } else {
            let parentKeys: string[] = [parentKey.recordKey];
            const hasPivot = selectedView?.pivot?.pivotPathKey;
            if (hasPivot && !fieldColumn.isPivot) {
                const metaLine = metaLines.get(position!.rowId);
                if (metaLine && metaLine.line.items.length > 0) {
                    const lineRecordKeys = getRecordKeys(metaLine, objectInfo!.object._key);
                    if (lineRecordKeys.length > 0) {
                        parentKeys = lineRecordKeys;
                    }
                }
            }
            if (parentKey.recordKey) {
                addLinksFromSeveralSources(workspace._key, parentKey.objectKey, relationKey, parentKeys, selectedRecordKey, direction).then((response) => {
                    if (response) {
                        if (isCompozzError(response)) {
                            const cError = response as CompozzError;
                            if (cError.i18n) {
                                toastError(translate(t, cError.i18n, cError.params));
                            } else {
                                toastError(cError.message);
                            }
                        } else {

                            if (onlyOneValue) {
                                updateValue(selectedItem);
                            } else {
                                let items: Item[] = [];
                                if (value.value) {
                                    if (isArray(value.value)) {
                                        items = value.value as Item[];
                                    } else {
                                        items = [value as Item];
                                    }
                                }
                                var newValues = (items) ? [...items, selectedItem] : [selectedItem];
                                const newItem: Item = { ...item, value: newValues };
                                updateValue(newItem);
                            }
                            setSearchValue("");
                        }
                    }
                });
            } else {
                fillPivot(workspace._key, position, fieldColumn, selectedView!, item, selectedItem, metaLines, fieldByPathKeys, objectInfo!, t, updateValue);
                setSearchValue("");
            }
        }
    }

    const linkedRecordKeys = existingLinks.map((link) => {
        if (direction === INBOUND) {
            return idToKey(link._from);
        } else {
            return idToKey(link._to);
        }
    });



    const onKeyDown = (event: React.KeyboardEvent) => {
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                setFocusedIndex((prev) => {
                    const next = Math.min(prev + 1, filteredRecords.length - 1);
                    virtualizer.scrollToIndex(next, { align: 'auto' });
                    return next;
                });
                break;
            case "ArrowUp":
                event.preventDefault();
                setFocusedIndex((prev) => {
                    const next = Math.max(prev - 1, 0);
                    virtualizer.scrollToIndex(next, { align: 'auto' });
                    return next;
                });
                break;
            case "Enter":
                event.preventDefault();
                if (filteredRecords.length > 0 && highlightedIndex >= 0 && highlightedIndex < filteredRecords.length) {
                    const line = filteredRecords[highlightedIndex];
                    const lineItem = displayedItem(line);
                    const recordKey = lineRecordKey(line);
                    if (lineItem && recordKey) {
                        onRecordSelect(lineItem, recordKey);
                    }
                }
                break;
            case "Escape":
                event.preventDefault();
                event.stopPropagation();
                setSearchValue("");
                break;
        }
    }

    const scrollAreaStyle = {
        scrollbarColor: wsColor + " " + wsLightColor,
        scrollbarThumbColor: wsDarkColor,
        scrollbarTrackColor: wsLightColor,
    }

    if (records === undefined) {
        return (
            <div>
                <h1>{t('data.table.loading')}</h1>
            </div>
        )
    } else if (records.length === 0) {
        return (
            <div>
                <h1>{t('data.table.noRecords')}</h1>
            </div>
        )
    } else {
        return (
            <TooltipProvider>
                <div onKeyDown={onKeyDown}>
                    <div className="flex flex-row justify-start items-center gap-2">
                        <Search className="w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder={t("data.record.search.placeholder")}
                            className={TW_CommonCommandInput}
                            autoFocus={true}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            ref={searchInputRef}
                        />
                    </div>
                    <Separator className="my-0.5" />
                    <div
                        ref={parentRef}
                        className={TW_SelectLinkedRecordScrollArea}
                        style={scrollAreaStyle}
                    >
                        {filteredRecords.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">
                                {t('data.table.noRecords')}
                            </div>
                        ) : (
                            <div
                                style={{
                                    height: `${virtualizer.getTotalSize()}px`,
                                    width: '100%',
                                    position: 'relative'
                                }}
                            >
                                {virtualizer.getVirtualItems().map((virtualItem) => {
                                    const line = filteredRecords[virtualItem.index];
                                    return (
                                        <div
                                            key={virtualItem.key}
                                            className="absolute top-0 left-0 w-full py-0.5 px-1"
                                            style={{
                                                height: `${virtualItem.size}px`,
                                                transform: `translateY(${virtualItem.start}px)`,
                                            }}
                                        >
                                            {line && <LinkedRecordSelectItem
                                                line={line}
                                                displayedItem={displayedItem(line)}
                                                recordKey={lineRecordKey(line)}
                                                linkedField={linkedField}
                                                parentKey={parentKey}
                                                linkedRecordKeys={linkedRecordKeys}
                                                onRecordSelect={onRecordSelect}
                                                format={format}
                                                isHighlighted={virtualItem.index === highlightedIndex}
                                            />
                                            }
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </TooltipProvider>
        )
    }
}

// displayedItem is the item of the displayed field of the line, recordKey the
// key of the target record the line stands for.
function LinkedRecordSelectItem({ line, displayedItem, recordKey, linkedField, parentKey, linkedRecordKeys, onRecordSelect, format, isHighlighted }: { line: Line, displayedItem: Item | undefined, recordKey: string | undefined, linkedField: Field | undefined, parentKey: ParentKey, linkedRecordKeys: string[], onRecordSelect: (item: Item, recordKey: string) => void, format: Map<string, any> | undefined, isHighlighted: boolean }) {
    const { t } = useTranslation();
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const useButtonHover = useHover(getWsButtonStyle(), getWsButtonStyle(wsLightColor, wsDarkColor));

    if (!displayedItem || !recordKey) {
        return null;
    }
    const fieldValue = itemToString(displayedItem, linkedField!, AggregationType.NO_AGGR, format, t, "", fieldByKeys);
    const isLinked = linkedRecordKeys.includes(recordKey);
    const values = line.items
        .filter((item) => {

            if (!item?.value || !item?.key?.fieldKey) {
                return false;
            }

            // Do not display the links
            if (item.parentKey?.recordKey) {
                return false;
            }

            const field = fieldByKeys?.get(item.key.fieldKey)!;
            if (!field) {
                return false;
            }

            return item !== displayedItem &&
                shouldDisplayInTooltip(field.type)
        }
        )
        .map((item) => {
            const field = fieldByKeys!.get(item.key.fieldKey)!;
            return valueToString(item.value, field, undefined, format, t, "", fieldByKeys);
        });
    const valuesStr = Array.from(values).join(" - ");
    const lineKey = parentKey.recordKey + "_" + recordKey;

    const selectedRecordStyle = {
        backgroundColor: wsLightColor,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: wsDarkColor,
    }
    const highlightedStyle = {
        backgroundColor: GRAY_100_COLOR,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: GRAY_200_COLOR,
    }
    const notLinkedStyle = {
        backgroundColor: useButtonHover.style.backgroundColor,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: GRAY_200_COLOR,
    }

    const getStyle = () => {
        if (isLinked) return selectedRecordStyle;
        if (isHighlighted) return highlightedStyle;
        return notLinkedStyle;
    }

    return (
        <button
            key={lineKey}
            className={`${isLinked ? TW_LinkedRecordCommandItemSelected : TW_LinkedRecordCommandItem}`}
            style={getStyle()}
            onClick={() => onRecordSelect(displayedItem, recordKey)}
            onMouseEnter={useButtonHover.onMouseEnter}
            onMouseLeave={useButtonHover.onMouseLeave}>

            <Tooltip>
                <TooltipTrigger asChild>
                    <div className={TW_LinkedRecordCommandItemTooltipTrigger}>
                        <span className={TW_LinkedRecordCommandItemFieldText}>{fieldValue}</span>
                        <span className={TW_LinkedRecordCommandItemRecordText}>{valuesStr}</span>
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <div className="flex flex-col">
                        <span className={TW_LinkedRecordCommandItemTooltipFieldText}>{fieldValue}</span>
                        <span className={TW_LinkedRecordCommandItemTooltipRecordText}>{valuesStr}</span>
                    </div>
                </TooltipContent>
            </Tooltip>
        </button>
    )
}